import { useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faPen, faToggleOff, faToggleOn } from '@fortawesome/free-solid-svg-icons';

import DataTable from '../../components/shared/DataTable';
import { Button, Label, TextField } from '../../components/shared/FormElements';
import { useAppContext } from '../../Context';
import { useAppDispatch } from '../../hooks';
import { setPopAlert } from '../../slices/miscSlice';
import { CompanyType } from '../../types';
import { usePermissions } from '../../services';

const ViewCompanies: React.FC = () => {
  const { apiCall, auth } = useAppContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { canView_company, canAdd_company, canChange_company, canView_menu_companies } = usePermissions([
    'view_company',
    'add_company',
    'change_company',
    'view_menu_companies',
  ]);

  const storedUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const isSuperuser = Boolean(auth.user?.is_superuser || storedUser?.is_superuser || storedUser?.username === 'adminraptor');

  const [companies, setCompanies] = useState<CompanyType[]>([]);
  const [activeCompanyId, setActiveCompanyId] = useState<number | null>(storedUser?.active_company?.id ?? null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    name: '',
    address: '',
    phone: '',
    website: '',
    email: '',
    is_active: true,
    admin_username: '',
    admin_email: '',
    admin_password: '',
    admin_first_name: '',
    admin_last_name: '',
  });

  const canViewCompanies = isSuperuser || canView_company || canView_menu_companies;
  const canCreateCompany = isSuperuser || canAdd_company;
  const canEditCompany = isSuperuser || canChange_company;

  useEffect(() => {
    if (!canViewCompanies) {
      navigate('/');
      return;
    }
    fetchCompanies();
    fetchActiveCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewCompanies]);

  const fetchCompanies = async () => {
    const response = await apiCall('GET', 'companies/', null, '', 'No se pudo obtener las empresas');
    if (response) {
      setCompanies(response);
    }
  };

  const fetchActiveCompany = async () => {
    const response = await apiCall('GET', 'companies/active/', null, '', 'No se pudo obtener la empresa activa');
    if (response) {
      setActiveCompanyId(response.active_company?.id ?? null);
      updateStoredUser({ active_company: response.active_company ?? null });
    }
  };

  const updateStoredUser = (updates: any) => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const nextUser = { ...currentUser, ...updates };
    localStorage.setItem('user', JSON.stringify(nextUser));
  };

  const resetForm = () => {
    setForm({
      name: '',
      address: '',
      phone: '',
      website: '',
      email: '',
      is_active: true,
      admin_username: '',
      admin_email: '',
      admin_password: '',
      admin_first_name: '',
      admin_last_name: '',
    });
    setLogoFile(null);
    setEditingId(null);
  };

  const handleSubmit = async () => {
    if (!editingId && !canCreateCompany) {
      dispatch(setPopAlert({ message: 'No tienes permisos para crear empresas', show: true, alertType: 'danger' }));
      return;
    }
    if (editingId && !canEditCompany) {
      dispatch(setPopAlert({ message: 'No tienes permisos para editar empresas', show: true, alertType: 'danger' }));
      return;
    }
    if (!form.name.trim()) {
      dispatch(setPopAlert({ message: 'El nombre de la empresa es requerido', show: true, alertType: 'danger' }));
      return;
    }

    if (!editingId) {
      if (!form.admin_username || !form.admin_email || !form.admin_password) {
        dispatch(
          setPopAlert({
            message: 'Usuario, email y contraseña del administrador son obligatorios',
            show: true,
            alertType: 'danger',
          })
        );
        return;
      }
    }

    const formData = new FormData();
    formData.append('name', form.name.trim());
    if (form.address) formData.append('address', form.address);
    if (form.phone) formData.append('phone', form.phone);
    if (form.website) formData.append('website', form.website);
    if (form.email) formData.append('email', form.email);
    formData.append('is_active', String(form.is_active));
    if (logoFile) {
      formData.append('logo', logoFile);
    }

    if (!editingId) {
      formData.append('admin_username', form.admin_username);
      formData.append('admin_email', form.admin_email);
      formData.append('admin_password', form.admin_password);
      if (form.admin_first_name) formData.append('admin_first_name', form.admin_first_name);
      if (form.admin_last_name) formData.append('admin_last_name', form.admin_last_name);
    }

    const url = editingId ? `companies/${editingId}/` : 'companies/';
    const method = editingId ? 'PUT' : 'POST';
    const success = editingId ? 'Empresa actualizada correctamente' : 'Empresa creada correctamente';
    const error = editingId ? 'No se pudo actualizar la empresa' : 'No se pudo crear la empresa';

    const response = await apiCall(method as any, url, formData, success, error, { isFormData: true });
    if (response) {
      resetForm();
      fetchCompanies();
    }
  };

  const handleEdit = (company: CompanyType) => {
    setEditingId(company.id);
    setForm({
      name: company.name ?? '',
      address: company.address ?? '',
      phone: company.phone ?? '',
      website: company.website ?? '',
      email: company.email ?? '',
      is_active: company.is_active ?? true,
      admin_username: '',
      admin_email: '',
      admin_password: '',
      admin_first_name: '',
      admin_last_name: '',
    });
    setLogoFile(null);
  };

  const handleToggleActive = async (company: CompanyType) => {
    const response = await apiCall(
      'PATCH',
      `companies/${company.id}/`,
      { is_active: !company.is_active },
      'Estado actualizado correctamente',
      'No se pudo actualizar el estado'
    );
    if (response) {
      fetchCompanies();
      if (!response.is_active && activeCompanyId === company.id) {
        setActiveCompanyId(null);
        updateStoredUser({ active_company: null });
      }
    }
  };

  const handleSetActiveCompany = async (company: CompanyType) => {
    if (company.is_active === false) {
      dispatch(setPopAlert({ message: 'La empresa está inactiva', show: true, alertType: 'danger' }));
      return;
    }
    const response = await apiCall(
      'PUT',
      'companies/active/',
      { company_id: company.id },
      'Empresa activa actualizada',
      'No se pudo actualizar la empresa activa'
    );
    if (response) {
      setActiveCompanyId(company.id);
      updateStoredUser({ active_company: response.active_company ?? null });
    }
  };

  const columns = [
    { title: 'ID', field: 'id' },
    { title: 'Logo', field: 'logo', type: 'image' },
    { title: 'Nombre', field: 'name' },
    { title: 'Email', field: 'email' },
    { title: 'Teléfono', field: 'phone' },
    { title: 'Web', field: 'website' },
    { title: 'Estado', field: 'is_active', type: 'decorator_bool', cell_class: 'text-center' },
  ];

  const actions = [
    canEditCompany
      ? {
          icon: <FontAwesomeIcon icon={faPen} />,
          tooltip: 'Editar',
          onClick: (row: CompanyType) => handleEdit(row),
        }
      : null,
    canEditCompany
      ? {
          icon: {
            field: 'is_active',
            options: {
              true: <FontAwesomeIcon icon={faToggleOff} />,
              false: <FontAwesomeIcon icon={faToggleOn} />,
            },
          },
          tooltip: {
            field: 'is_active',
            options: {
              true: 'Inactivar',
              false: 'Activar',
            },
          },
          onClick: (row: CompanyType) => handleToggleActive(row),
        }
      : null,
    canEditCompany
      ? {
          icon: <FontAwesomeIcon icon={faBolt} />,
          tooltip: 'Usar como empresa activa',
          onClick: (row: CompanyType) => handleSetActiveCompany(row),
          condition: { field: 'id', operator: '!==', value: activeCompanyId ?? 0 },
        }
      : null,
  ];

  return (
    <div className='container-wrap'>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={8} className='text-left'>
          <h2>Empresas</h2>
          {activeCompanyId ? (
            <p className='text-log'>Empresa activa: {companies.find((c) => c.id === activeCompanyId)?.name ?? '-'}</p>
          ) : (
            <p className='text-log'>No hay empresa activa seleccionada</p>
          )}
        </Grid>
        <Grid item xs={12} sm={4} className='text-right'>
          {editingId ? (
            <Button className='btn btn-secondary' onClick={resetForm}>
              Cancelar edición
            </Button>
          ) : null}
        </Grid>
      </Grid>

      <div className='form-container'>
        <h3>{editingId ? 'Editar empresa' : 'Crear nueva empresa'}</h3>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <div className='form-group'>
              <Label htmlFor='company_name'>Nombre</Label>
              <TextField
                id='company_name'
                value={form.name}
                onChange={(e: any) => setForm({ ...form, name: e.target.value })}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className='form-group'>
              <Label htmlFor='company_email'>Email</Label>
              <TextField
                id='company_email'
                value={form.email}
                onChange={(e: any) => setForm({ ...form, email: e.target.value })}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className='form-group'>
              <Label htmlFor='company_phone'>Teléfono</Label>
              <TextField
                id='company_phone'
                value={form.phone}
                onChange={(e: any) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className='form-group'>
              <Label htmlFor='company_website'>Sitio web</Label>
              <TextField
                id='company_website'
                value={form.website}
                onChange={(e: any) => setForm({ ...form, website: e.target.value })}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className='form-group'>
              <Label htmlFor='company_address'>Dirección</Label>
              <TextField
                id='company_address'
                value={form.address}
                onChange={(e: any) => setForm({ ...form, address: e.target.value })}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <div className='form-group'>
              <Label htmlFor='company_logo'>Logo</Label>
              <input
                id='company_logo'
                type='file'
                accept='image/*'
                onChange={(e: any) => setLogoFile(e.target.files?.[0] || null)}
              />
            </div>
          </Grid>
          {!editingId ? (
            <>
              <Grid item xs={12} sm={6}>
                <div className='form-group'>
                  <Label htmlFor='admin_username'>Usuario administrador</Label>
                  <TextField
                    id='admin_username'
                    value={form.admin_username}
                    onChange={(e: any) => setForm({ ...form, admin_username: e.target.value })}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <div className='form-group'>
                  <Label htmlFor='admin_email'>Email administrador</Label>
                  <TextField
                    id='admin_email'
                    value={form.admin_email}
                    onChange={(e: any) => setForm({ ...form, admin_email: e.target.value })}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <div className='form-group'>
                  <Label htmlFor='admin_password'>Contraseña administrador</Label>
                  <TextField
                    id='admin_password'
                    type='password'
                    value={form.admin_password}
                    onChange={(e: any) => setForm({ ...form, admin_password: e.target.value })}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <div className='form-group'>
                  <Label htmlFor='admin_first_name'>Nombre administrador</Label>
                  <TextField
                    id='admin_first_name'
                    value={form.admin_first_name}
                    onChange={(e: any) => setForm({ ...form, admin_first_name: e.target.value })}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <div className='form-group'>
                  <Label htmlFor='admin_last_name'>Apellido administrador</Label>
                  <TextField
                    id='admin_last_name'
                    value={form.admin_last_name}
                    onChange={(e: any) => setForm({ ...form, admin_last_name: e.target.value })}
                  />
                </div>
              </Grid>
            </>
          ) : null}
          <Grid item xs={12} sm={12} className='text-right'>
            {(editingId ? canEditCompany : canCreateCompany) && (
              <Button className='btn btn-primary' onClick={handleSubmit}>
                {editingId ? 'Actualizar empresa' : 'Crear empresa'}
              </Button>
            )}
          </Grid>
        </Grid>
      </div>

      <DataTable data={companies} columns={columns} actions={actions} />
    </div>
  );
};

export default ViewCompanies;
